import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const TOKEN_KEY = 'intranet_token';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let onUnauthorized = null;
export const setOnUnauthorized = (fn) => { onUnauthorized = fn; };

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem('intranet_user');
      onUnauthorized?.();
    }
    return Promise.reject(err);
  }
);

export const getMe = () => api.get('/auth/me');
export const completeMicrosoftSignup = (data) => api.post('/auth/microsoft/complete', data);

// Staff endpoints
export const getStaff = () => api.get('/staff');
export const getStaffById = (id) => api.get(`/staff/${id}`);
export const addStaff = (data) => api.post('/staff', data);
export const updateProfile = (id, data) => api.put(`/staff/${id}`, data);
export const setAdminStatus = (id, data) => api.put(`/staff/${id}/admin`, data);
export const setMythicalStatus = (id, data) => api.put(`/staff/${id}/mythical`, data);
export const deleteStaffAccount = (id, requesterId) => api.delete(`/staff/${id}`, { data: { requester_id: requesterId } });

// Announcements endpoints
export const getAnnouncements = (staffId) => api.get('/announcements', { params: { staff_id: staffId } });
export const getAnnouncementById = (id) => api.get(`/announcements/${id}`);
export const createAnnouncement = (data) => api.post('/announcements', data);
export const likeAnnouncement = (id, staffId) => api.post(`/announcements/${id}/like`, { staff_id: staffId });
export const unlikeAnnouncement = (id, staffId) => api.delete(`/announcements/${id}/like`, { data: { staff_id: staffId } });
export const reactToAnnouncement = (id, staffId, emoji) => api.post(`/announcements/${id}/like`, { staff_id: staffId, emoji });
export const unreactToAnnouncement = (id, staffId) => api.delete(`/announcements/${id}/like`, { data: { staff_id: staffId } });
export const reactToComment = (announcementId, commentId, staffId, emoji) => api.post(`/announcements/${announcementId}/comment/${commentId}/react`, { staff_id: staffId, emoji });
export const unreactToComment = (announcementId, commentId, staffId) => api.delete(`/announcements/${announcementId}/comment/${commentId}/react`, { data: { staff_id: staffId } });
export const commentOnAnnouncement = (id, data) => api.post(`/announcements/${id}/comment`, data);
export const replyToComment = (announcementId, parentId, data) =>
  api.post(`/announcements/${announcementId}/comment`, { ...data, parent_id: parentId });
export const updateAnnouncement = (id, data) => api.put(`/announcements/${id}`, data);
export const pinAnnouncement = (id, data) => api.put(`/announcements/${id}/pinned`, data);
export const reorderPinnedAnnouncements = (updates) => api.put('/announcements/reorder-pinned', updates);
export const deleteAnnouncement = (id, data = {}) => api.delete(`/announcements/${id}`, { data });
export const deleteAnnouncementComment = (announcementId, commentId, staffId) => api.delete(`/announcements/${announcementId}/comment/${commentId}`, { data: { staff_id: staffId } });
export const updateAnnouncementComment = (announcementId, commentId, data) => api.put(`/announcements/${announcementId}/comment/${commentId}`, data);
export const getAnnouncementLikers = (id) => api.get(`/announcements/${id}/likes`);
export const approveAnnouncement = (id, staffId) => api.post(`/announcements/${id}/approve`, { staff_id: staffId });
export const denyAnnouncement = (id, staffId, note) => api.post(`/announcements/${id}/deny`, { staff_id: staffId, note });

// Calendar endpoints
export const getCalendarEvents = () => api.get('/calendar');
export const getCalendarEventsByRange = (start, end) => api.get(`/calendar/range/${start}/${end}`);
export const createCalendarEvent = (data) => api.post('/calendar', data);
export const updateCalendarEvent = (id, data) => api.put(`/calendar/${id}`, data);
export const deleteCalendarEvent = (id, data = {}) => api.delete(`/calendar/${id}`, data);

// Quick links endpoints
// Timesheet endpoints
export const getMyTimesheet = (staffId, periodStart, periodEnd) =>
  api.get(`/timesheet/my?staff_id=${staffId}&period_start=${periodStart}&period_end=${periodEnd}`);
export const getTimesheetSummary = (requesterId, periodStart, periodEnd) =>
  api.get(`/timesheet/summary?requester_id=${requesterId}&period_start=${periodStart}&period_end=${periodEnd}`);
export const getStaffTimesheet = (requesterId, staffId, periodStart, periodEnd) =>
  api.get(`/timesheet/staff/${staffId}?requester_id=${requesterId}&period_start=${periodStart}&period_end=${periodEnd}`);
export const saveTimesheetEntry = (data) => api.post('/timesheet', data);
export const deleteTimesheetEntry = (id, staffId) =>
  api.delete(`/timesheet/${id}`, { data: { staff_id: staffId } });

// Attachment endpoints
export const uploadAttachments = (announcementId, formData) =>
  api.post(`/attachments/announcements/${announcementId}`, formData, {
    headers: { 'Content-Type': undefined },
  });
export const deleteAttachment = (id, staffId) =>
  api.delete(`/attachments/${id}`, { data: { staff_id: staffId } });

// Presence endpoints
export const getPresenceToday = () => api.get('/presence/today');
export const getStaffPresenceToday = (staffId) => api.get(`/presence/staff/${staffId}`);
export const getMyPresence = () => api.get('/presence/my');
export const presenceHeartbeat = () => api.post('/presence/heartbeat');
export const setPresenceOverride = (data) => api.post('/presence/override', data);
export const removePresenceOverride = (data) => api.delete('/presence/override', { data });
// Office wifi networks (admin)
export const getWhoami = () => api.get('/presence/whoami');
export const getOfficeNetworks = () => api.get('/presence/office-networks');
export const addOfficeNetwork = (data) => api.post('/presence/office-networks', data);
export const removeOfficeNetwork = (ip) => api.delete(`/presence/office-networks/${encodeURIComponent(ip)}`);

// Quick links endpoints
export const getQuickLinks = () => api.get('/links');
export const createQuickLink = (data) => api.post('/links', data);
export const updateQuickLink = (id, data) => api.put(`/links/${id}`, data);
export const deleteQuickLink = (id) => api.delete(`/links/${id}`);
export const reorderQuickLinks = (updates) => api.patch('/links/reorder', updates);

// Notification endpoints
export const getNotifications = (staffId) => api.get(`/notifications?staff_id=${staffId}`);
export const getUnreadCount = (staffId) => api.get(`/notifications/unread-count?staff_id=${staffId}`);
export const markNotificationRead = (id, staffId) => api.put(`/notifications/${id}/read`, { staff_id: staffId });
export const markAllNotificationsRead = (staffId) => api.put('/notifications/read-all', { staff_id: staffId });
export const getNotificationPreferences = (staffId) => api.get(`/notifications/preferences?staff_id=${staffId}`);
export const updateNotificationPreferences = (data) => api.put('/notifications/preferences', data);

// Poll endpoints
export const createPoll = (data) => api.post('/polls', data);
export const getPoll = (type, id, staffId) => api.get(`/polls/for/${type}/${id}`, { params: { staff_id: staffId } });
export const votePoll = (pollId, data) => api.post(`/polls/${pollId}/vote`, data);
export const retractPollVote = (pollId, staffId) => api.delete(`/polls/${pollId}/vote`, { data: { staff_id: staffId } });

// Analytics endpoints
export const getAnalyticsOverview = (requesterId) => api.get('/analytics/overview', { params: { requester_id: requesterId } });
export const getAnalyticsTimeseries = (requesterId, days = 14) => api.get('/analytics/timeseries', { params: { requester_id: requesterId, days } });
export const getActivityFeed = (requesterId, params = {}) => api.get('/analytics/feed', { params: { requester_id: requesterId, ...params } });

// Birthdays
export const getBirthdaysToday = () => api.get('/staff/birthdays/today');
export const getUpcomingBirthdays = () => api.get('/staff/birthdays/upcoming');

// Weather
export const getWeather = (office) => api.get(`/weather?office=${encodeURIComponent(office)}`);

// Bug reports
export const submitBugReport = (data) => api.post('/bug-reports', data);
export const getBugReports = () => api.get('/bug-reports');
export const updateBugReportStatus = (id, status) => api.put(`/bug-reports/${id}/status`, { status });

export default api;
